const SEVEN_DAYS_MS = 7 * 86_400_000;

const MIN_RUNS_IN_WINDOW = 3;
const MIN_FAILURES_FOR_RATE = 3;
const FAILURE_RATE_THRESHOLD = 0.5;
const MIN_CONSECUTIVE_FAILURES = 2;

export type RunStatus = "running" | "success" | "error";

export type MonitorWorkflowInput = {
  id: string;
  name: string;
  spaceId: string;
  pausedAt: number | null;
  deletedAt?: number | null;
  branches?: {
    id: string;
    headCommitId: string | null;
    workflowRuns?: ReadonlyArray<MonitorWorkflowRunInput>;
  } | null;
};

export type MonitorWorkflowRunInput = {
  id: string;
  createdAt: number;
  runStatus?: RunStatus | null;
};

export type MonitoringRunDailyStatInput = {
  workflowId: string;
  day: number;
  runCount: number | null;
  errorRunCount: number | null;
};

export type FailingWorkflowRow = {
  workflowId: string;
  spaceId: string;
  name: string;
  failureDurationMs: number;
  failedRunCount: number;
  totalRunCount: number;
  consecutiveFailures: number;
  lastFailureAt: number;
  lastSuccessAt: number | null;
};

export function formatFailureDuration(ms: number): string {
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h`;
  const minutes = Math.max(1, Math.floor(ms / 60_000));
  return `${minutes}m`;
}

// The monitorSpaceWorkflows query already limits the branch to main and its
// workflowRuns to non-ephemeral runs.
function mainBranchRuns(workflow: MonitorWorkflowInput) {
  const main = workflow.branches;
  if (!main?.headCommitId) return null;
  return main.workflowRuns ?? [];
}

// Live-run state (latest status, streaks, last success) comes from the synced
// runs; the 7-day failed/total counts and the rate criterion come from the
// workflow_run_daily_stats rollup, which is exact at any run volume where the
// 14-run window truncates. The run-volume threshold doubles as the activity
// gate: emitted workflows necessarily ran within the window.
// Per-run statuses come from workflow_runs.run_status. A missing status (a run
// predating the column's backfill window, whose optimistic insert hasn't
// round-tripped) reads as running, so it can neither flag nor clear a streak.
export function computeFailingWorkflows(
  workflows: ReadonlyArray<MonitorWorkflowInput>,
  dailyStats: ReadonlyArray<MonitoringRunDailyStatInput>,
  now = Date.now()
): FailingWorkflowRow[] {
  const windowStart = now - SEVEN_DAYS_MS;
  const windowBucket = windowStart - (windowStart % 86_400_000);

  const counts = new Map<string, { failed: number; total: number }>();
  for (const stat of dailyStats) {
    if (stat.day < windowBucket) continue;
    const entry = counts.get(stat.workflowId) ?? { failed: 0, total: 0 };
    entry.failed += stat.errorRunCount ?? 0;
    entry.total += stat.runCount ?? 0;
    counts.set(stat.workflowId, entry);
  }

  const rows: FailingWorkflowRow[] = [];
  for (const workflow of workflows) {
    if (workflow.deletedAt != null || workflow.pausedAt != null) continue;

    const runs = mainBranchRuns(workflow);
    if (!runs) continue;

    const completedOnMain = runs
      .map((r) => ({
        createdAt: r.createdAt,
        status: r.runStatus ?? ("running" as RunStatus),
      }))
      .filter((r) => r.status !== "running")
      .sort((a, b) => b.createdAt - a.createdAt);
    const completedInWindow = completedOnMain.filter(
      (r) => r.createdAt >= windowStart
    );

    const latestCompleted = completedInWindow[0];
    if (!latestCompleted || latestCompleted.status !== "error") continue;

    let consecutiveFailures = 0;
    for (const run of completedInWindow) {
      if (run.status !== "error") break;
      consecutiveFailures++;
    }

    const { failed: failedRunCount, total: totalRunCount } = counts.get(
      workflow.id
    ) ?? { failed: 0, total: 0 };
    if (totalRunCount < MIN_RUNS_IN_WINDOW) continue;

    const meetsStreak = consecutiveFailures >= MIN_CONSECUTIVE_FAILURES;
    const meetsRate =
      failedRunCount >= MIN_FAILURES_FOR_RATE &&
      failedRunCount / totalRunCount >= FAILURE_RATE_THRESHOLD;
    if (!meetsStreak && !meetsRate) continue;

    const lastSuccessAt =
      completedOnMain.find((r) => r.status === "success")?.createdAt ?? null;
    const streakStart =
      completedInWindow[consecutiveFailures - 1]?.createdAt ??
      latestCompleted.createdAt;
    const failureDurationMs = lastSuccessAt
      ? now - lastSuccessAt
      : now - streakStart;

    rows.push({
      workflowId: workflow.id,
      spaceId: workflow.spaceId,
      name: workflow.name,
      failureDurationMs,
      failedRunCount,
      totalRunCount,
      consecutiveFailures,
      lastFailureAt: latestCompleted.createdAt,
      lastSuccessAt,
    });
  }

  return rows;
}

export type FailingWorkflowSort = "duration" | "volume";

export function sortFailingWorkflows(
  rows: readonly FailingWorkflowRow[],
  sort: FailingWorkflowSort
): FailingWorkflowRow[] {
  const list = [...rows];
  list.sort((a, b) => {
    const primary =
      sort === "volume"
        ? b.failedRunCount - a.failedRunCount ||
          b.failureDurationMs - a.failureDurationMs
        : b.failureDurationMs - a.failureDurationMs ||
          b.failedRunCount - a.failedRunCount;
    if (primary !== 0) return primary;
    return a.name.localeCompare(b.name);
  });
  return list;
}

// Daily count of failed main-branch runs over the last `days`, oldest bucket
// first.
export function computeFailureTrend(
  rows: ReadonlyArray<
    Pick<MonitoringRunDailyStatInput, "day" | "errorRunCount">
  >,
  now = Date.now(),
  days = 30
): number[] {
  const buckets = new Array<number>(days).fill(0);
  for (const row of rows) {
    const daysAgo = Math.floor((now - row.day) / 86_400_000);
    const index = days - 1 - daysAgo;
    if (index >= 0 && index < days) {
      buckets[index] = (buckets[index] ?? 0) + (row.errorRunCount ?? 0);
    }
  }
  return buckets;
}
